import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { useAppDispatch } from "@redux/hooks";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { ROLE_META } from "@user-management-context/shared/onboarding/role-meta";
import { setActiveRole } from "@user-management-context/write/application/use-cases/active-role/set-active-role";
import { writeActiveRoleToStorage } from "@shared-kernel/core/active-role/active-role";
import { clearMultiRoleOnboardingFlow, markMultiRoleOnboardingFlow } from "@shared-kernel/core/onboarding/multi-role-flow";
import { Badge } from "@shared-kernel/primary/shared/shadcn/ui/badge";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";

const ROLE_PRIORITY: ROLES[] = [ROLES.STUDENT, ROLES.TEACHER, ROLES.PROVIDER];

const sortByPriority = (a: ROLES, b: ROLES) => ROLE_PRIORITY.indexOf(a) - ROLE_PRIORITY.indexOf(b);

export const OnboardingOverviewPage = () => {
  const { user } = useSession();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  if (!user) return <Navigate to="/login" replace />;
  if (user.isAdmin) return <Navigate to="/" replace />;

  const pendingRoles = user.roles
    .filter(r => r.status === "pending_onboarding")
    .map(r => r.role)
    .sort(sortByPriority);
  const doneRoles = user.roles
    .filter(r => r.status === "onboarded")
    .map(r => r.role)
    .sort(sortByPriority);

  if (pendingRoles.length === 0) {
    clearMultiRoleOnboardingFlow();
    return <Navigate to="/" replace />;
  }
  if (pendingRoles.length === 1) {
    return <Navigate to={`/onboarding/${pendingRoles[0]}`} replace />;
  }
  markMultiRoleOnboardingFlow();

  const handleStart = (role: ROLES) => {
    dispatch(setActiveRole(role));
    writeActiveRoleToStorage(role);
    navigate(`/onboarding/${role}`);
  };

  return (
    <ul className="space-y-3">
      {pendingRoles.map(role => {
        const meta = ROLE_META[role];
        return (
          <li key={role}>
            <button
              type="button"
              onClick={() => handleStart(role)}
              className={cn(
                "group flex w-full items-center gap-4 rounded-lg border bg-card p-5 text-left shadow-sm transition",
                "hover:border-primary hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              )}
            >
              <span className="flex size-12 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                <meta.Icon className="size-6" strokeWidth={1.75} />
              </span>
              <div className="flex-1 space-y-1">
                <p className="text-base font-semibold">{meta.label}</p>
                <p className="text-sm text-muted-foreground">{meta.description}</p>
              </div>
              <ArrowRight className="size-5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
            </button>
          </li>
        );
      })}

      {doneRoles.map(role => {
        const meta = ROLE_META[role];
        return (
          <li key={role}>
            <div className="flex w-full items-center gap-4 rounded-lg border border-transparent bg-muted/40 p-5 text-muted-foreground">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-md bg-accent/60">
                <meta.Icon className="size-6" strokeWidth={1.75} />
              </span>
              <div className="flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-base font-semibold line-through">{meta.label}</p>
                  <Badge variant="outline">Terminé</Badge>
                </div>
                <p className="text-sm">{meta.description}</p>
              </div>
              <Check className="size-5 shrink-0 text-primary/60" />
            </div>
          </li>
        );
      })}
    </ul>
  );
};
